import React from 'react';
import { Link } from 'react-router-dom';
import { BedDoubleIcon, BathIcon, HeartIcon, ImagesIcon, MapPinIcon, ShieldCheckIcon, StarIcon, UsersIcon } from 'lucide-react';
import type { Listing } from '../../types/housing';
import { Button } from '../ui/Button';
import { verificationCopy } from '../../utils/housing';
import { distanceLabel, roommateSignal } from '../../utils/housingSignals';
interface PropertyCardProps {
  listing: Listing;
  /** 'row' is the wide editorial card; 'stacked' is the narrow/mobile card. */
  layout?: 'row' | 'stacked';
  selected?: boolean;
  saved: boolean;
  onToggleSave: (id: string) => void;
  onHover?: (id: string | null) => void;
  onSelect?: (id: string) => void;
}
const FACILITY_CAP = 4;
export function PropertyCard({
  listing,
  layout = 'row',
  selected = false,
  saved,
  onToggleSave,
  onHover,
  onSelect
}: PropertyCardProps) {
  const inspected = listing.verification === 'verified';
  const trust = verificationCopy(listing);
  const roommates = roommateSignal(listing);
  const extraFacilities = listing.facilities.length - FACILITY_CAP;
  return <article onMouseEnter={() => onHover?.(listing.id)} onMouseLeave={() => onHover?.(null)} onFocus={() => onHover?.(listing.id)} onClick={() => onSelect?.(listing.id)} className={`group overflow-hidden rounded-2xl border bg-surface transition-shadow duration-150 ease-out ${selected ? 'border-brand-700 shadow-md ring-1 ring-brand-700' : 'border-line hover:shadow-md'} ${layout === 'row' ? 'sm:flex' : ''}`} data-id="element-1927" data-mp="3134zd9k3j6mq">
      {/* Photography is the first thing a person reads on a home. */}
      <div className={`relative shrink-0 ${layout === 'row' ? 'sm:w-64 lg:w-72' : ''}`} data-id="element-1928" data-mp="2jokcerjxdaz">
        <img src={listing.image} alt={`${listing.name}, ${listing.neighbourhood}`} loading="lazy" className={`w-full object-cover ${layout === 'row' ? 'h-48 sm:h-full' : 'h-48'}`} data-id="element-1929" data-mp="24btabkphq0j2" />
        <button type="button" onClick={event => {
        event.stopPropagation();
        onToggleSave(listing.id);
      }} aria-pressed={saved} aria-label={saved ? `Remove ${listing.name} from your shortlist` : `Save ${listing.name} to your shortlist`} className="absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-full bg-surface/95 text-ink shadow-sm transition-colors duration-150 ease-out hover:bg-surface" data-id="element-1930" data-mp="2p1nh6at2lcg7">
          <HeartIcon aria-hidden className={`h-5 w-5 ${saved ? 'fill-danger-700 text-danger-700' : 'text-ink-muted'}`} data-id="element-1931" data-mp="3c09vx83v3aq4" />
        </button>

        {inspected ? <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-surface/95 px-2.5 py-1 text-caption font-medium text-ok-800 shadow-sm" data-id="element-1932" data-mp="3lq34s1cmdbqo">
            <ShieldCheckIcon aria-hidden className="h-4 w-4" data-id="element-1933" data-mp="3vp2e7m57lbeq" />
            Inspected property
          </span> : <span className="absolute bottom-3 left-3 rounded-full bg-surface/95 px-2.5 py-1 text-caption font-medium text-ink-muted shadow-sm" data-id="element-1934" data-mp="1k309cml5dh8c">
            Not inspected by Campuzon
          </span>}

        <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-ink/70 px-2 py-1 text-caption font-medium text-white" data-id="element-1935" data-mp="1a4khgheaq3xs">
          <ImagesIcon aria-hidden className="h-3.5 w-3.5" data-id="element-1936" data-mp="22sgly8a3x054" />
          <span className="cz-num" data-id="element-1937" data-mp="22rx2gn4t63oz">1 / 12</span>
        </span>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-3 p-4" data-id="element-1938" data-mp="1xuetiwqsjogz">
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1" data-id="element-1939" data-mp="1t2nad79ysl0v">
          <div className="min-w-0" data-id="element-1940" data-mp="1t0anqgt9hmhh">
            <h3 className="text-body-lg font-semibold text-ink" data-id="element-1941" data-mp="1o4c416ivosne">
              <Link to={`/housing/${listing.id}`} className="rounded" onClick={event => event.stopPropagation()} data-id="element-1942" data-mp="2liy27ddr639l">
                {listing.name}
              </Link>
            </h3>
            <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-body-sm text-ink-muted" data-id="element-1943" data-mp="2196qm3yjyvyy">
              <MapPinIcon aria-hidden className="h-4 w-4 shrink-0" data-id="element-1944" data-mp="26a2tqfb2ye3b" />
              <span data-id="element-1945" data-mp="1rck7bjlvkcz1">
                {listing.neighbourhood}, Lagos
              </span>
              <span aria-hidden data-id="element-1946" data-mp="1wavh5uc66nh9">·</span>
              <span className="text-brand-700" data-id="element-1947" data-mp="1wbnaeyiao94d">{distanceLabel(listing)}</span>
            </p>
          </div>
          <p className="flex items-center gap-1 text-body-sm text-ink" data-id="element-1948" data-mp="2k17rtcysuybj">
            <StarIcon aria-hidden className="h-4 w-4 fill-warn-700 text-warn-700" data-id="element-1949" data-mp="2oyr6gdzetd40" />
            <span className="cz-num font-medium" data-id="element-1950" data-mp="2abfu49velp2u">{listing.rating}</span>
            <span className="cz-num text-ink-subtle" data-id="element-1951" data-mp="2f9zkcmh15n10">({listing.reviewCount})</span>
          </p>
        </div>

        <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-body-sm text-ink-muted" data-id="element-1952" data-mp="32ztx741cnbl9">
          <li className="flex items-center gap-1.5" data-id="element-1953" data-mp="37q0iatwnkdyr">
            <BedDoubleIcon aria-hidden className="h-4 w-4" data-id="element-1954" data-mp="37rv3m9bu683d" />
            {listing.kind}
          </li>
          <li className="flex items-center gap-1.5" data-id="element-1955" data-mp="w9qgps2m8zfa">
            <UsersIcon aria-hidden className="h-4 w-4" data-id="element-1956" data-mp="rcqfbugpokui" />
            {listing.occupants} per room
          </li>
          <li className="flex items-center gap-1.5" data-id="element-1957" data-mp="161e6lojiy8en">
            <BathIcon aria-hidden className="h-4 w-4" data-id="element-1958" data-mp="162xn9g93elgy" />
            {listing.roomType}
          </li>
        </ul>

        <ul className="flex flex-wrap gap-2" data-id="element-1959" data-mp="dcfnop7kv28a">
          {listing.facilities.slice(0, FACILITY_CAP).map(facility => <li key={facility} className="rounded-full border border-line px-2.5 py-1 text-caption text-ink-muted" data-id="element-1960" data-mp="8e5ps6ugxniz">
              {facility}
            </li>)}
          {extraFacilities > 0 && <li className="rounded-full border border-line px-2.5 py-1 text-caption text-ink-muted" data-id="element-1961" data-mp="idmtwju2ntcu">
              +{extraFacilities}
            </li>}
        </ul>

        {/* The Campuzon difference: a home plus the people in it. */}
        {roommates ? <div className="flex flex-wrap items-center gap-3 rounded-xl border border-brand-100 bg-brand-50 px-3 py-2" data-id="element-1962" data-mp="qf00hw1gs36r">
            <span className="flex -space-x-2" data-id="element-1963" data-mp="vd1kqkamm8jt">
              {roommates.initials.map(initials => <span key={initials} aria-hidden className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-surface bg-brand-700 text-caption font-semibold text-white" data-id="element-1964" data-mp="10er02c4vt8dp">
                  {initials}
                </span>)}
            </span>
            <span className="min-w-0" data-id="element-1965" data-mp="gpgh8p9ua7bd">
              <span className="block text-body-sm font-medium text-ink" data-id="element-1966" data-mp="gof2oatm72ow">
                {listing.bedsAvailable} {listing.bedsAvailable === 1 ? 'room' : 'rooms'} available ·{' '}
                {roommates.count} potential {roommates.count === 1 ? 'roommate' : 'roommates'}
              </span>
              <span className="block text-caption text-ink-muted" data-id="element-1967" data-mp="3p48o942sz4ct">
                <span className="cz-num" data-id="element-1968" data-mp="3hmitkx0b18nf">{roommates.averageScore}%</span> average compatibility on
                your lifestyle and housing preferences — not a guarantee.
              </span>
            </span>
          </div> : <p className="rounded-xl border border-line bg-canvas px-3 py-2 text-caption text-ink-muted" data-id="element-1969" data-mp="38jrg6rnvc2t5">
            {listing.bedsAvailable} {listing.bedsAvailable === 1 ? 'room' : 'rooms'} available ·
            nobody looking to share here yet.
          </p>}

        <div className="mt-auto flex flex-wrap items-end justify-between gap-3 pt-1" data-id="element-1970" data-mp="2tvn5a9v24vkx">
          <p data-id="element-1971" data-mp="2owtskjnlh8lt">
            <span className="cz-num block text-h4 font-semibold text-ink" data-id="element-1972" data-mp="2oymwhr6tpeq4">
              ₦{listing.rentPerYear.toLocaleString()}
            </span>
            <span className="block text-caption text-ink-muted" data-id="element-1973" data-mp="2ku6y0azjh9kx">
              per year · available {listing.availableFrom}
            </span>
          </p>
          <Link to={`/housing/${listing.id}`} onClick={event => event.stopPropagation()} data-id="element-1974" data-mp="2az5471ubjjc0">
            <Button variant="primary" size="sm" data-id="element-1975" data-mp="2q71iistzz1l">
              View details
            </Button>
          </Link>
        </div>

        {!inspected && <p className="text-caption text-ink-subtle" data-id="element-1976" data-mp="7luu786ivabu">{trust.note}</p>}
      </div>
    </article>;
}